// skipcq: JS-W1028
import { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';

import { computePortfolioCompleteness } from '../../utils/brewhousePortfolio/computePortfolioCompleteness';

const TRACKER_RAIL_WIDTH_PX = 240;

function CompletenessBar({ percent, height = 8 }) {
  const clampedPercent = Math.min(100, Math.max(0, percent));

  return (
    <Box
      sx={{
        height,
        borderRadius: 1,
        bgcolor: 'grey.200',
        overflow: 'hidden',
      }}
      role="progressbar"
      aria-valuenow={clampedPercent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${clampedPercent}% complete`}
    >
      <Box
        sx={{
          height: '100%',
          width: `${clampedPercent}%`,
          bgcolor: 'primary.main',
          transition: 'width 200ms ease',
        }}
      />
    </Box>
  );
}

CompletenessBar.propTypes = {
  percent: PropTypes.number.isRequired,
  height: PropTypes.number,
};

function TrackerDetails({ completeness }) {
  const helperText =
    completeness.missingDescriptionCount > 0
      ? `Please add descriptions for the ${completeness.missingDescriptionCount} remaining ${completeness.missingDescriptionCount === 1 ? 'beer' : 'beers'} in your portfolio.`
      : 'Every beer in this portfolio has a description.';

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Description
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {completeness.descriptionPercent}%
          </Typography>
        </Box>
        <CompletenessBar percent={completeness.descriptionPercent} height={6} />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: 'Lora, Georgia, serif',
            fontWeight: 600,
            mb: 1,
          }}
        >
          Library Complete
        </Typography>
        <Typography variant="h5" component="p" fontWeight={700} sx={{ mb: 1 }}>
          {completeness.descriptionPercent}%
        </Typography>
        <CompletenessBar percent={completeness.descriptionPercent} height={10} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {helperText}
        </Typography>
      </Box>
    </Box>
  );
}

TrackerDetails.propTypes = {
  completeness: PropTypes.shape({
    descriptionPercent: PropTypes.number.isRequired,
    missingDescriptionCount: PropTypes.number.isRequired,
  }).isRequired,
};

function PintPortfolioStatusTracker({ beers, loading = false, layout = 'band' }) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [xsExpanded, setXsExpanded] = useState(false);

  const completeness = useMemo(
    () => computePortfolioCompleteness(beers),
    [beers],
  );

  const showCollapsedSummary = layout === 'band' && isXs && !isMdUp;
  const showDetails = layout === 'rail' || !showCollapsedSummary || xsExpanded;

  const handleToggleExpanded = useCallback(() => {
    setXsExpanded((previous) => !previous);
  }, []);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        bgcolor: 'background.paper',
        width: layout === 'rail' ? TRACKER_RAIL_WIDTH_PX : '100%',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showDetails ? 2 : 0 }}>
          <Typography
            variant="subtitle2"
            component="h2"
            sx={{
              fontFamily: 'Lora, Georgia, serif',
              fontWeight: 600,
            }}
          >
            Beer Status Tracker
          </Typography>
          {showCollapsedSummary && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {completeness.descriptionPercent}%
              </Typography>
              <IconButton
                size="small"
                onClick={handleToggleExpanded}
                aria-expanded={xsExpanded}
                aria-label={xsExpanded ? 'Collapse status tracker' : 'Expand status tracker'}
                sx={{
                  transform: xsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 200ms ease',
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {showCollapsedSummary ? (
          <Collapse in={xsExpanded}>
            <TrackerDetails completeness={completeness} />
          </Collapse>
        ) : (
          <TrackerDetails completeness={completeness} />
        )}
      </CardContent>
    </Card>
  );
}

PintPortfolioStatusTracker.propTypes = {
  beers: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  layout: PropTypes.oneOf(['band', 'rail']),
};

export default PintPortfolioStatusTracker;
